// src/components/ViewCameras.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import CameraFeed from "../components/CameraFeed";
import LiveLogs from "../components/LiveLogs";
import TaskTableRow from "../components/TasksTable";
import { toast } from "react-toastify";
import "./ViewCameras.css";
import "../components/TasksTable.css";

import {
  getAllInstancesStatus,
  stopSpecificInstance,
  stopAudioStream,
  listAllAudioTasks,
  PREDEFINED_MODES_MAP,
} from "../Api";

// Helper to create a unique ID for audio tasks
const getUniqueAudioTaskId = (task) => {
  if (!task || !task.streamId || !task.type) return null;
  return `${task.streamId}_${task.type}`;
};

// Session storage helpers
const getSessionState = (key, defaultValue) => {
  const item = sessionStorage.getItem(key);
  try {
    return item ? JSON.parse(item) : defaultValue;
  } catch (e) {
    console.warn("Error parsing session state for key:", key, e);
    return defaultValue;
  }
};
const setSessionState = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn("Error stringifying session state for key:", key, e);
  }
};

const RELOAD_FLAG_KEY = "viewCameras_hasReloadedForSelection";

const ViewCameras = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialLoadRef = useRef(true);
  const mountedRef = useRef(false);

  const [activeVideoInstances, setActiveVideoInstances] = useState(() => getSessionState("viewCameras_activeVideoInstances", []));
  const [activeAudioTasks, setActiveAudioTasks] = useState(() => getSessionState("viewCameras_activeAudioTasks", []));
  const [singlePassAudioResult, setSinglePassAudioResult] = useState(() => getSessionState("viewCameras_singlePassAudioResult", null));
  const [selectedPrimaryInstanceId, setSelectedPrimaryInstanceId] = useState(() => getSessionState("viewCameras_selectedPrimaryInstanceId", null));
  const [primaryDisplayData, setPrimaryDisplayData] = useState(null);
  const [playbackFileObject, setPlaybackFileObject] = useState(null);
  const [playbackRemoteUrl, setPlaybackRemoteUrl] = useState(() => getSessionState("viewCameras_playbackRemoteUrl", null));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Keep last stable primary to avoid flashing when backend briefly returns empty lists
  const lastStablePrimaryRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    if (sessionStorage.getItem(RELOAD_FLAG_KEY) === "true") {
      sessionStorage.removeItem(RELOAD_FLAG_KEY);
    }
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => { if (mountedRef.current) setSessionState("viewCameras_activeVideoInstances", activeVideoInstances); }, [activeVideoInstances]);
  useEffect(() => { if (mountedRef.current) setSessionState("viewCameras_activeAudioTasks", activeAudioTasks); }, [activeAudioTasks]);
  useEffect(() => { if (mountedRef.current) setSessionState("viewCameras_singlePassAudioResult", singlePassAudioResult); }, [singlePassAudioResult]);
  useEffect(() => { if (mountedRef.current) setSessionState("viewCameras_selectedPrimaryInstanceId", selectedPrimaryInstanceId); }, [selectedPrimaryInstanceId]);
  useEffect(() => { if (mountedRef.current) setSessionState("viewCameras_playbackRemoteUrl", playbackRemoteUrl); }, [playbackRemoteUrl]);

  const clearAllActiveAudioStreamingStates = useCallback(() => {
    if (!mountedRef.current) return;
    setActiveAudioTasks([]);
    setPlaybackFileObject(null);
  }, []);

  const clearSinglePassAudioState = useCallback((typeToClear = "all") => {
    if (!mountedRef.current) return;
    setSinglePassAudioResult(prev => {
        if (typeToClear === "all" || (prev && prev.analysisType === typeToClear)) return null;
        return prev;
    });
    if (typeToClear === "all" || (singlePassAudioResult && singlePassAudioResult.analysisType === typeToClear)){
        setPlaybackFileObject(null);
        setPlaybackRemoteUrl(null);
    }
  }, [singlePassAudioResult]);

  // Effect to derive primaryDisplayData
  useEffect(() => {
    if (!mountedRef.current) return;
    
    const navState = location.state;
    console.log("[PrimaryDisplayEffect] Running. Selected ID:", selectedPrimaryInstanceId, "NavState:", navState);

    // Priority 1: A newly started task from navigation state.
    if (navState) {
        if (navState.audioStreamId && navState.audioTaskType) {
            const uniqueId = `${navState.audioStreamId}_${navState.audioTaskType}`;
            const isBounce = navState.audioTaskType.includes('bounce');
            const displayNamePrefix = isBounce ? "Bounce Detector" : "Customer Service Monitor";
            
            setPrimaryDisplayData({
                type: 'audio_streaming',
                instanceId: uniqueId,
                displayName: `${displayNamePrefix} (${navState.micDisplayName || navState.audioOriginalSource})`,
                status: { is_running: true, current_mode: displayNamePrefix, message: "Connecting..." },
                streamUrl: navState.audioVideoStreamUrl,
                isRtspAudioActive: navState.audioTaskType.includes('rtsp'),
                rtspOriginalUrl: navState.audioTaskType.includes('rtsp') ? navState.audioOriginalSource : null,
                modeForLogs: displayNamePrefix,
                audioStreamIdForLogs: navState.audioStreamId,
                audioTaskTypeForLogs: navState.audioTaskType,
                ddbSourceIdForLogs: navState.audioOriginalSource,
                isBounceDisplay: isBounce,
            });
            setSelectedPrimaryInstanceId(uniqueId);
            console.log("[PrimaryDisplayEffect] Set primary display directly from navState (Audio).");
            return;
        }

        const videoInstance = navState.newlyStartedInstance || navState.justCalibratedInstance;
        if (videoInstance?.instance_id) {
             const displayModeKey = Object.keys(PREDEFINED_MODES_MAP).find(key => PREDEFINED_MODES_MAP[key] === videoInstance.mode) || videoInstance.mode;
             setPrimaryDisplayData({
                type: 'video',
                instanceId: videoInstance.instance_id,
                displayName: `${displayModeKey} (${videoInstance.camera_id})`,
                status: { is_running: true, current_mode: videoInstance.mode, message: "Starting..." },
                streamUrl: videoInstance.stream_url,
                modeForLogs: displayModeKey,
                videoInstanceIdForLogs: videoInstance.instance_id,
                ddbSourceIdForLogs: videoInstance.camera_id,
             });
             setSelectedPrimaryInstanceId(videoInstance.instance_id);
             console.log("[PrimaryDisplayEffect] Set primary display directly from navState (Video).");
             return;
        }
    }

    // Priority 2: An existing selection ID from polled data.
    if (!selectedPrimaryInstanceId) {
      lastStablePrimaryRef.current = null;
      setPrimaryDisplayData(null);
      return;
    }

    const videoMatch = activeVideoInstances.find(inst => inst.instance_id === selectedPrimaryInstanceId);
    if (videoMatch) {
      const displayModeKey = Object.keys(PREDEFINED_MODES_MAP).find(key => PREDEFINED_MODES_MAP[key] === videoMatch.mode) || videoMatch.mode;
      const nextPrimary = {
        type: 'video',
        instanceId: videoMatch.instance_id,
        displayName: `${displayModeKey} (${videoMatch.camera_id || 'N/A'})`,
        status: { is_running: (videoMatch.status === 'running' || videoMatch.status === 'started'), current_mode: videoMatch.mode, message: videoMatch.message },
        streamUrl: videoMatch.stream_url,
        modeForLogs: displayModeKey,
        videoInstanceIdForLogs: videoMatch.instance_id,
        ddbSourceIdForLogs: videoMatch.camera_id,
      };
      lastStablePrimaryRef.current = nextPrimary;
      setPrimaryDisplayData(nextPrimary);
      return;
    }

    const audioMatch = activeAudioTasks.find(task => getUniqueAudioTaskId(task) === selectedPrimaryInstanceId);
    if (audioMatch) {
      const isBounce = audioMatch.type?.includes('bounce');
      const displayNamePrefix = isBounce ? "Bounce Detector" : "Customer Service Monitor";
      const nextPrimary = {
        type: 'audio_streaming',
        instanceId: selectedPrimaryInstanceId,
        displayName: `${displayNamePrefix} (${audioMatch.source || 'N/A'})`,
        status: { is_running: audioMatch.isRunning, current_mode: displayNamePrefix, message: audioMatch.message, status: audioMatch.status },
        streamUrl: audioMatch.videoUrl,
        playbackSource: playbackFileObject || playbackRemoteUrl,
        isRtspAudioActive: audioMatch.type?.includes('rtsp') && audioMatch.isRunning,
        rtspOriginalUrl: audioMatch.type?.includes('rtsp') ? audioMatch.source : null,
        modeForLogs: displayNamePrefix,
        audioStreamIdForLogs: audioMatch.streamId,
        audioTaskTypeForLogs: audioMatch.type,
        ddbSourceIdForLogs: audioMatch.source,
        isBounceDisplay: isBounce,
      };
      lastStablePrimaryRef.current = nextPrimary;
      setPrimaryDisplayData(nextPrimary);
      return;
    }

    // No match found; keep last stable data to prevent UI flashing
    if (lastStablePrimaryRef.current) {
      console.log("[PrimaryDisplayEffect] No match found; keeping last stable primary display to avoid flicker.");
      setPrimaryDisplayData(lastStablePrimaryRef.current);
    } else {
      setPrimaryDisplayData(null);
    }

  }, [selectedPrimaryInstanceId, activeVideoInstances, activeAudioTasks, location.state, playbackFileObject, playbackRemoteUrl]);

  const fetchVideoStatuses = useCallback(async () => {
    if (!mountedRef.current) return [];
    try {
      const response = await getAllInstancesStatus();
      if (!mountedRef.current) return []; 
      if (response && response.instances && Array.isArray(response.instances)) {
        const videoInst = response.instances
          .filter(inst => 
            inst.mode !== "Audio Sentiment Analysis" && 
            inst.mode !== "Bounce Audio Analysis" && 
            inst.mode !== "AI Detection"
          )
          .map(inst => ({
            instance_id: inst.instance_id,
            mode: inst.mode, 
            camera_id: inst.camera_id,
            stream_url: inst.stream_url,
            log_file_path: inst.log_file_path,
            input_source_path: inst.input_source_path,
            status: inst.status, 
            message: inst.message,
            modeDisplay: (Object.keys(PREDEFINED_MODES_MAP).find(key => PREDEFINED_MODES_MAP[key] === inst.mode)) || inst.mode,
          }));
        if (mountedRef.current) { 
            setActiveVideoInstances(prevInstances =>
                JSON.stringify(prevInstances) !== JSON.stringify(videoInst) ? videoInst : prevInstances
            );
        }
        return videoInst;
      }
      // Do NOT clear instances when response is unexpected - keeps UI stable during transient issues
    } catch (err) {
      console.error("[ViewCameras] Fetch video statuses error:", err);
      // Do NOT clear instances on error to prevent UI flashing
      // if (mountedRef.current) setActiveVideoInstances([]);
    }
    return [];
  }, []);

  const fetchAudioTasksCombined = useCallback(async (navStateForInitialLoad) => {
    if (!mountedRef.current) return [];
    try {
      const response = await listAllAudioTasks(); 
      if (!mountedRef.current) return [];

      const allAudioTasksRaw = response.tasks || [];
      console.log("[fetchAudioTasksCombined] Raw tasks from API:", JSON.stringify(allAudioTasksRaw));
      
      const newAudioTasksUI = allAudioTasksRaw.map(task => {
        let videoUrlFromNav = null;
        if (
          navStateForInitialLoad &&
          task.stream_id === navStateForInitialLoad.audioStreamId
        ) {
          videoUrlFromNav = navStateForInitialLoad.audioVideoStreamUrl;
        }

        return {
          type: task.type, 
          streamId: task.stream_id,
          uniqueItemId: getUniqueAudioTaskId(task),
          source: task.source,
          camera_display_name: task.camera_display_name,
          videoUrl: task.video_stream_url || videoUrlFromNav,
          logFile: task.log_file,
          isRunning: task.is_running,
          status: task.status,
          message: task.message,
          analysis_mode: task.analysis_mode,  // Include analysis mode from API response
        };
      });

      if (mountedRef.current) {
        let tasksToReturn = newAudioTasksUI;
        setActiveAudioTasks(prevTasks => {
            const shouldSkipClearing = newAudioTasksUI.length === 0 && prevTasks.length > 0;
            if (shouldSkipClearing) {
                console.warn("[fetchAudioTasksCombined] API returned empty tasks; keeping previous list to avoid UI flicker.");
                tasksToReturn = prevTasks;
                return prevTasks;
            }
            const isChanged = JSON.stringify(prevTasks) !== JSON.stringify(newAudioTasksUI);
            tasksToReturn = isChanged ? newAudioTasksUI : prevTasks;
            return tasksToReturn;
        });

        if (navStateForInitialLoad?.audioStreamId && navStateForInitialLoad?.audioTaskType) {
            const uniqueNavId = `${navStateForInitialLoad.audioStreamId}_${navStateForInitialLoad.audioTaskType}`;
            const matchedTask = newAudioTasksUI.find(t => t.uniqueItemId === uniqueNavId);

            if (matchedTask) {
                const taskTypeFromNav = navStateForInitialLoad.audioTaskType || ''; 
                const isLocalType = taskTypeFromNav.includes('local_chunked');
                const isRemoteType = taskTypeFromNav.includes('remote_chunked') || taskTypeFromNav.includes('remote_single_pass');

                if (isLocalType && navStateForInitialLoad.playbackFileObject) {
                    setPlaybackFileObject(navStateForInitialLoad.playbackFileObject);
                    setPlaybackRemoteUrl(null);
                } else if (isRemoteType && navStateForInitialLoad.playbackRemoteUrl) {
                    setPlaybackRemoteUrl(navStateForInitialLoad.playbackRemoteUrl);
                    setPlaybackFileObject(null);
                }
            }
        }
        return tasksToReturn;
      }
      return newAudioTasksUI;
    } catch (e) {
      console.error("[ViewCameras] Fetch combined audio tasks error:", e);
      // Do NOT clear tasks on error to prevent UI flashing
      // if (mountedRef.current) setActiveAudioTasks([]);
    }
    return [];
  }, []);

  const selectDefaultPrimary = useCallback((videos, audios, currentSinglePassResult, navStateFromLocation) => {
    if (!mountedRef.current) return;
    const currentSessionSelectedId = getSessionState("viewCameras_selectedPrimaryInstanceId", null);
    console.log("[selectDefaultPrimary] Called. Videos:", videos.length, "Audios:", audios.length, "SinglePass:", !!currentSinglePassResult, "NavState:", navStateFromLocation, "CurrentSelectedId (from state):", selectedPrimaryInstanceId, "CurrentSelectedId (from session):", currentSessionSelectedId);

    if (navStateFromLocation) {
      if (navStateFromLocation.newlyStartedInstance?.instance_id) {
        const exists = videos.some(v => v.instance_id === navStateFromLocation.newlyStartedInstance.instance_id && (v.status === 'running' || v.status === 'started'));
        if (exists) {
          console.log("[selectDefaultPrimary] Selecting from navState (newlyStartedInstance):", navStateFromLocation.newlyStartedInstance.instance_id);
          setSelectedPrimaryInstanceId(navStateFromLocation.newlyStartedInstance.instance_id); return;
        }
      }
      if (navStateFromLocation.justCalibratedInstance?.instance_id) {
         const exists = videos.some(v => v.instance_id === navStateFromLocation.justCalibratedInstance.instance_id && (v.status === 'running' || v.status === 'started'));
        if (exists) {
            console.log("[selectDefaultPrimary] Selecting from navState (justCalibratedInstance):", navStateFromLocation.justCalibratedInstance.instance_id);
            setSelectedPrimaryInstanceId(navStateFromLocation.justCalibratedInstance.instance_id); return;
        }
      }
      if (navStateFromLocation.audioStreamId && navStateFromLocation.audioTaskType) {
        const uniqueNavAudioId = `${navStateFromLocation.audioStreamId}_${navStateFromLocation.audioTaskType}`;
        const existsInFetchedAudios = audios.some(a => getUniqueAudioTaskId(a) === uniqueNavAudioId && a.isRunning);
        if (existsInFetchedAudios) {
            console.log("[selectDefaultPrimary] Selecting from navState (uniqueAudioId found in fetched tasks):", uniqueNavAudioId);
            setSelectedPrimaryInstanceId(uniqueNavAudioId); return;
        } else {
            console.log("[selectDefaultPrimary] uniqueAudioId from navState not found or not running in fetched audios list yet.");
        }
      }
      if (navStateFromLocation.audioTaskType === 'remote_single_pass' && navStateFromLocation.analysisResult) {
        setSelectedPrimaryInstanceId("single-pass-audio"); return;
      }
      if (navStateFromLocation.audioTaskType === 'remote_single_pass_bounce' && navStateFromLocation.analysisResult) {
        setSelectedPrimaryInstanceId("single-pass-bounce-audio"); return;
      }
    }

    const idToConsider = currentSessionSelectedId || selectedPrimaryInstanceId; 
    if (idToConsider) {
      const isVideo = videos.some(v => v.instance_id === idToConsider && (v.status === 'running' || v.status === 'started'));
      const isAudio = audios.some(a => getUniqueAudioTaskId(a) === idToConsider && a.isRunning);
      const isSinglePassSentiment = idToConsider === "single-pass-audio" && currentSinglePassResult && currentSinglePassResult.analysisType === 'sentiment';
      const isSinglePassBounce = idToConsider === "single-pass-bounce-audio" && currentSinglePassResult && currentSinglePassResult.analysisType === 'bounce';

      if (isVideo || isAudio || isSinglePassSentiment || isSinglePassBounce) {
        console.log("[selectDefaultPrimary] Maintaining/Restoring selection:", idToConsider);
        if (selectedPrimaryInstanceId !== idToConsider) setSelectedPrimaryInstanceId(idToConsider);
        return;
      }
    }

    const firstRunningVideo = videos.find(v => v.status === 'running' || v.status === 'started');
    if (firstRunningVideo) {
      setSelectedPrimaryInstanceId(firstRunningVideo.instance_id); return;
    }

    const firstRunningAudio = audios.find(a => a.isRunning);
    if (firstRunningAudio) {
      const uniqueAudioId = getUniqueAudioTaskId(firstRunningAudio);
      if (uniqueAudioId) {
        setSelectedPrimaryInstanceId(uniqueAudioId); return;
      }
    }

    if (currentSinglePassResult) {
        const idForSinglePass = currentSinglePassResult.analysisType === 'bounce' ? "single-pass-bounce-audio" : "single-pass-audio";
        setSelectedPrimaryInstanceId(idForSinglePass); return;
    }
    
    if (selectedPrimaryInstanceId !== null) setSelectedPrimaryInstanceId(null);
  }, [selectedPrimaryInstanceId]); 

  useEffect(() => {
    const currentNavState = location.state;
    let pollerId;

    const initializeAndPoll = async () => {
      if (!mountedRef.current) return;
      console.log("[InitializeAndPoll] Starting. InitialLoad:", initialLoadRef.current, "NavState:", currentNavState);
      setError(null);
      
      let fetchedVideos = activeVideoInstances;
      let fetchedAudios = activeAudioTasks;
      let currentSinglePass = singlePassAudioResult;

      if (initialLoadRef.current) {
        setIsLoading(true);
        const isSentimentSinglePass = currentNavState?.audioTaskType === 'remote_single_pass' && currentNavState.analysisResult;
        const isBounceSinglePass = currentNavState?.audioTaskType === 'remote_single_pass_bounce' && currentNavState.analysisResult;

        if (isSentimentSinglePass || isBounceSinglePass) {
          if (mountedRef.current) {
            const analysisType = isBounceSinglePass ? 'bounce' : 'sentiment';
            const newSinglePassData = {...currentNavState.analysisResult, analysisType};
            setSinglePassAudioResult(newSinglePassData);
            currentSinglePass = newSinglePassData;
            if (currentNavState.playbackRemoteUrl) setPlaybackRemoteUrl(currentNavState.playbackRemoteUrl);
            if (currentNavState.playbackFileObject) setPlaybackFileObject(currentNavState.playbackFileObject);
            clearAllActiveAudioStreamingStates(); fetchedAudios = [];
            setActiveVideoInstances([]); fetchedVideos = [];
          }
        } else {
          fetchedVideos = await fetchVideoStatuses();
          fetchedAudios = await fetchAudioTasksCombined(currentNavState);
        }
        
        // Let the primary display effect handle selection from navState
        if (!currentNavState) {
            selectDefaultPrimary(fetchedVideos, fetchedAudios, currentSinglePass, null);
        }
        
        if (mountedRef.current) {
          initialLoadRef.current = false; 
          if (currentNavState && navigate) {
            console.log("[InitializeAndPoll] Clearing navState.");
            navigate(location.pathname, { replace: true, state: null });
          }
          setIsLoading(false);
        }
      }

      pollerId = setInterval(async () => {
        if (!mountedRef.current) { clearInterval(pollerId); return; }
        const latestVideos = await fetchVideoStatuses();
        const latestAudios = await fetchAudioTasksCombined(null);
        if (!selectedPrimaryInstanceId) {
            selectDefaultPrimary(latestVideos, latestAudios, singlePassAudioResult, null);
        }
      }, 7500);
    };

    initializeAndPoll();
    return () => { 
      if (pollerId) clearInterval(pollerId);
    };
  }, [location.state, navigate, fetchVideoStatuses, fetchAudioTasksCombined, selectDefaultPrimary, clearAllActiveAudioStreamingStates, singlePassAudioResult, selectedPrimaryInstanceId]);

  const handleStopProcess = async (uniqueInstanceIdToStop, processType) => {
    if (!mountedRef.current) return;
    setIsLoading(true); setError(null);
    let itemTypeDescription = "Process";
    try {
      let success = false;
      let baseStreamIdForApi = uniqueInstanceIdToStop;

      if (processType === 'audio_streaming' && uniqueInstanceIdToStop) {
        const taskToStop = activeAudioTasks.find(t => getUniqueAudioTaskId(t) === uniqueInstanceIdToStop);
        if (taskToStop) {
          baseStreamIdForApi = taskToStop.streamId;
          const isBounce = taskToStop.type?.includes('bounce');
          itemTypeDescription = isBounce ? "Bounce Detector task" : "Customer Service Monitor task";
          await stopAudioStream(baseStreamIdForApi, isBounce ? "bounce" : "sentiment");
          toast.info(`Stop signal sent for ${itemTypeDescription}: ${baseStreamIdForApi.slice(-6)}`);
          if (mountedRef.current) {
            setActiveAudioTasks(prev => prev.filter(task => getUniqueAudioTaskId(task) !== uniqueInstanceIdToStop));
            if (primaryDisplayData?.type === 'audio_streaming' && primaryDisplayData.instanceId === uniqueInstanceIdToStop) {
               if (primaryDisplayData.playbackSource instanceof File) setPlaybackFileObject(null);
            }
          }
          success = true;
        }
      } else if (processType === 'video' && uniqueInstanceIdToStop) {
        itemTypeDescription = "video instance";
        await stopSpecificInstance(uniqueInstanceIdToStop);
        toast.info(`Stop signal sent for ${itemTypeDescription}: ${uniqueInstanceIdToStop.slice(-6)}`);
        if (mountedRef.current) {
          setActiveVideoInstances(prev => prev.filter(inst => inst.instance_id !== uniqueInstanceIdToStop));
        }
        success = true;
      } else if (processType === 'audio_static') {
        const isBounceStatic = uniqueInstanceIdToStop === "single-pass-bounce-audio";
        itemTypeDescription = isBounceStatic ? "single-pass Bounce Detector result" : "single-pass Customer Service result";
        toast.info(`Cleared ${itemTypeDescription}.`);
        if (mountedRef.current) {
          clearSinglePassAudioState(isBounceStatic ? "bounce" : "sentiment");
        }
        success = true;
      } else {
        itemTypeDescription = "unknown process type";
        toast.warn("No specific process identified to stop/clear.");
      }

      if (success && mountedRef.current) {
        if (selectedPrimaryInstanceId === uniqueInstanceIdToStop) {
           const currentVids = (processType === 'video') ? activeVideoInstances.filter(inst => inst.instance_id !== uniqueInstanceIdToStop) : activeVideoInstances;
           const currentAuds = (processType === 'audio_streaming') ? activeAudioTasks.filter(task => getUniqueAudioTaskId(task) !== uniqueInstanceIdToStop) : activeAudioTasks;
           const currentSPass = (processType === 'audio_static') ? null : singlePassAudioResult;
           selectDefaultPrimary(currentVids, currentAuds, currentSPass, null);
        }
      }
      setTimeout(async () => {
          if (!mountedRef.current) return;
          const v = await fetchVideoStatuses();
          const a = await fetchAudioTasksCombined(null);
          selectDefaultPrimary(v, a, singlePassAudioResult, null);
          if (mountedRef.current) setIsLoading(false);
      }, 1800); 
    } catch (err) {
      console.error("[ViewCameras] Stop/Clear error:", err);
      if (mountedRef.current) { setError(err.message); setIsLoading(false); }
      toast.error(`Failed to stop/clear ${itemTypeDescription}: ${err.message}`);
    }
  };

  // Helper functions for Stop All functionality
  const getTotalActiveProcesses = () => {
    return activeVideoInstances.length + activeAudioTasks.length;
  };

  const hasMultipleProcesses = () => {
    const total = getTotalActiveProcesses();
    return total > 1 || (total > 0 && !primaryDisplayData);
  };

  // Stop All Processes Handler
  const handleStopAllProcesses = async () => {
    if (!mountedRef.current) return;
    
    const totalProcesses = getTotalActiveProcesses();
    if (totalProcesses === 0) {
      toast.info("No active processes to stop.");
      return;
    }

    setIsLoading(true);
    setError(null);
    
    let successCount = 0;
    let failureCount = 0;
    const errors = [];

    toast.info(`Stopping all processes (${totalProcesses})...`);

    try {
      // Stop all video instances
      for (const videoInstance of activeVideoInstances) {
        try {
          await stopSpecificInstance(videoInstance.instance_id);
          successCount++;
          toast.success(`Stopped ${videoInstance.modeDisplay || videoInstance.mode} (...${videoInstance.instance_id.slice(-6)})`);
        } catch (err) {
          failureCount++;
          errors.push(`${videoInstance.modeDisplay || videoInstance.mode}: ${err.message}`);
          console.error(`Failed to stop video instance ${videoInstance.instance_id}:`, err);
        }
      }

      // Stop all audio tasks
      for (const audioTask of activeAudioTasks) {
        try {
          const isBounce = audioTask.type?.includes('bounce');
          await stopAudioStream(audioTask.streamId, isBounce ? "bounce" : "sentiment");
          successCount++;
          const taskName = isBounce ? "Bounce Detector" : "Customer Service Monitor";
          toast.success(`Stopped ${taskName} (...${audioTask.streamId.slice(-6)})`);
        } catch (err) {
          failureCount++;
          const taskName = audioTask.type?.includes('bounce') ? "Bounce Detector" : "Customer Service Monitor";
          errors.push(`${taskName}: ${err.message}`);
          console.error(`Failed to stop audio task ${audioTask.streamId}:`, err);
        }
      }

      // Clear all state
      if (mountedRef.current) {
        setActiveVideoInstances([]);
        setActiveAudioTasks([]);
        setSelectedPrimaryInstanceId(null);
        setPrimaryDisplayData(null);
        clearSinglePassAudioState("all");
        setPlaybackFileObject(null);
        setPlaybackRemoteUrl(null);
      }

      // Show summary
      if (successCount > 0) {
        toast.success(`✅ Stop All completed: ${successCount} of ${totalProcesses} processes stopped successfully.`);
      }
      
      if (failureCount > 0) {
        toast.error(`❌ ${failureCount} processes failed to stop. Check individual error messages.`);
        if (errors.length > 0) {
          console.error("Stop All errors:", errors);
        }
      }

      // Refresh data after a brief delay to confirm all stopped
      setTimeout(async () => {
        if (!mountedRef.current) return;
        await fetchVideoStatuses();
        await fetchAudioTasksCombined(null);
        if (mountedRef.current) setIsLoading(false);
      }, 2000);

    } catch (err) {
      console.error("[ViewCameras] Stop All error:", err);
      if (mountedRef.current) {
        setError(err.message);
        setIsLoading(false);
      }
      toast.error(`Failed to stop all processes: ${err.message}`);
    }
  };

  const feedProps = primaryDisplayData ? {
    displayName: primaryDisplayData.displayName,
    status: primaryDisplayData.status,
    streamUrl: primaryDisplayData.streamUrl,
    playbackSource: primaryDisplayData.playbackSource,
    isRtspAudioActive: primaryDisplayData.isRtspAudioActive,
    rtspOriginalUrl: primaryDisplayData.rtspOriginalUrl,
    isBounceDisplay: primaryDisplayData.isBounceDisplay,
    isAudioOnly:
   primaryDisplayData.type === "audio_streaming" &&
    !primaryDisplayData.isRtspAudioActive,
 } : {
   displayName: "No Active Process",
   status: { is_running: false, message: "N/A" }
 };
  
  // Extract current analysis mode for audio tasks
  const getCurrentAnalysisMode = () => {
    if (!primaryDisplayData || primaryDisplayData.type !== 'audio_streaming') {
      return null;
    }

    // Find the matching audio task to get analysis_mode
    const audioTask = activeAudioTasks.find(task =>
      getUniqueAudioTaskId(task) === primaryDisplayData.instanceId
    );

    return audioTask?.analysis_mode || null;
  };

  const logProps = primaryDisplayData ? {
    currentMode: primaryDisplayData.modeForLogs,
    analysisResult: primaryDisplayData.analysisResultForLogs,
    videoInstanceId: primaryDisplayData.videoInstanceIdForLogs,
    audioStreamId: primaryDisplayData.audioStreamIdForLogs,
    audioTaskType: primaryDisplayData.audioTaskTypeForLogs,
    ddbSourceId: primaryDisplayData.ddbSourceIdForLogs,
    currentAnalysisMode: getCurrentAnalysisMode(),
    enableHistoricalPolling: false,
  } : {
    currentMode: "No Active Process",
    enableHistoricalPolling: false,
  };

  const stopButtonUIText = primaryDisplayData
    ? primaryDisplayData.type === 'audio_static'
      ? `Clear ${primaryDisplayData.displayName.split(' (')[0]}`
      : `Stop ${primaryDisplayData.displayName.split('(')[0].trim()} (...${
          primaryDisplayData.instanceId ? 
          (primaryDisplayData.type === 'audio_streaming' ? primaryDisplayData.instanceId.split('_')[0].slice(-6) : primaryDisplayData.instanceId.slice(-6)) 
          : 'N/A'
        })`
    : "Stop Process";

  const allDisplayableItems = [
    ...activeVideoInstances.map(inst => ({
      key: inst.instance_id, 
      id: inst.instance_id,
      type: 'video',
      name: inst.modeDisplay || inst.mode,
      sourceInfo: inst.camera_id || inst.input_source_path?.split('/').pop() || "Video Source",
      status: inst.status,
      message: inst.message,
      streamUrl: inst.stream_url,
      logFile: inst.log_file_path,
      isRunning: inst.status === 'running' || inst.status === 'started',
      backendMode: inst.mode,
      instanceData: inst,
    })),
    ...activeAudioTasks.map(task => {
      const isBounceTask = task.type?.includes('bounce');
      const taskDisplayName = isBounceTask ? "Bounce Detector" : "Customer Service Monitor";
      const backendModeName = PREDEFINED_MODES_MAP[taskDisplayName] || (isBounceTask ? "Bounce Audio Analysis" : "Audio Sentiment Analysis");
      const uniqueItemId = getUniqueAudioTaskId(task);

      return {
        key: uniqueItemId,
        id: uniqueItemId,
        typeForTable: task.type,
        type: 'audio_streaming',
        name: taskDisplayName, 
        sourceInfo: task.source?.length > 30 ? task.source.substring(0, 27) + "..." : task.source,
        camera_display_name: task.camera_display_name,
        status: task.status, 
        message: task.message,
        videoUrl: task.videoUrl,
        logFile: task.logFile,
        isRunning: task.isRunning, 
        backendMode: backendModeName, 
        instanceData: task, 
      };
    }),
  ];

  if (singlePassAudioResult) {
    const isBounceSinglePass = singlePassAudioResult.analysisType === 'bounce';
    const singlePassId = isBounceSinglePass ? "single-pass-bounce-audio" : "single-pass-audio";
    const singlePassName = isBounceSinglePass ? "Bounce Detector (Result)" : "Customer Service Monitor (Result)";
    const singlePassBackendMode = isBounceSinglePass ? PREDEFINED_MODES_MAP["Bounce Detector"] : PREDEFINED_MODES_MAP["Customer Service Monitor"];
    allDisplayableItems.push({
      key: singlePassId, 
      id: singlePassId, 
      type: 'audio_static',
      name: singlePassName,
      sourceInfo: playbackRemoteUrl || (playbackFileObject ? playbackFileObject.name : (singlePassAudioResult.original_source_path || "Analyzed File")),
      status: 'completed',
      message: "Single-pass analysis complete.",
      isRunning: false,
      backendMode: singlePassBackendMode,
      instanceData: singlePassAudioResult,
    });
  }

  const handleSelectItem = useCallback((itemId) => {
    const fullItem = allDisplayableItems.find(i => i.id === itemId);
    const previousSelectedId = selectedPrimaryInstanceId;
    console.log(`[handleSelectItem] Clicked unique item ID: ${itemId}. Previous: ${previousSelectedId}. FullItem found: ${!!fullItem}`);

    if (fullItem) {
        setSelectedPrimaryInstanceId(itemId); 

        if (fullItem.type === 'audio_streaming' || fullItem.type === 'audio_static') {
            const instanceData = fullItem.instanceData;
            const effectiveType = instanceData?.type || (fullItem.type === 'audio_static' ? instanceData?.analysisType : null);

            if (effectiveType?.includes('local_chunked') || ((effectiveType === 'sentiment' || effectiveType === 'bounce') && instanceData?.original_source_path && !instanceData.original_source_path.startsWith('http'))) {
                setPlaybackRemoteUrl(null);
            } else if (effectiveType?.includes('remote_chunked') || ((effectiveType === 'sentiment' || effectiveType === 'bounce') && instanceData?.original_source_path?.startsWith('http'))) {
                const remoteUrlToSet = instanceData.original_source_path || instanceData.source;
                setPlaybackRemoteUrl(remoteUrlToSet);
                setPlaybackFileObject(null);
            } else if (effectiveType?.includes('rtsp')) { 
                 setPlaybackRemoteUrl(null);
                 setPlaybackFileObject(null);
            }
        } else if (fullItem.type === 'video') {
            setPlaybackRemoteUrl(null);
            setPlaybackFileObject(null);
        }
    } else {
        setSelectedPrimaryInstanceId(itemId); 
    }

    if (previousSelectedId !== itemId && itemId !== null) {
      const hasReloadedPreviouslyThisSession = sessionStorage.getItem(RELOAD_FLAG_KEY) === "true";
      if (!hasReloadedPreviouslyThisSession) {
        console.log(`[handleSelectItem] Different item selected (${previousSelectedId} -> ${itemId}). Preparing for page reload.`);
        sessionStorage.setItem(RELOAD_FLAG_KEY, "true");
        setTimeout(() => {
          if (mountedRef.current) window.location.reload();
        }, 150); 
      } else {
        console.log("[handleSelectItem] Page has already reloaded for a selection change in this visit. Skipping reload.");
      }
    }
  }, [allDisplayableItems, selectedPrimaryInstanceId]);

  return (
    <div className="app-container">
      <Sidebar />
      <main className="main-section">
        <section className="camera-page-grid">
          <div className="feed-and-controls-area">
            <CameraFeed key={primaryDisplayData?.instanceId || "default-feed"} {...feedProps} />
            <div className="process-control" style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
              {/* Individual Stop Button */}
              {primaryDisplayData && (
                <button
                  className="stop-button process-btn stop"
                  onClick={() => handleStopProcess(primaryDisplayData.instanceId, primaryDisplayData.type)}
                  disabled={isLoading}
                >
                  {isLoading ? "Processing…" : stopButtonUIText}
                </button>
              )}
              
              {/* Stop All Button */}
              {hasMultipleProcesses() && (
                <button
                  className="stop-button process-btn stop"
                  onClick={handleStopAllProcesses}
                  disabled={isLoading}
                  style={{ 
                    backgroundColor: "#d32f2f", 
                    border: "1px solid #b71c1c",
                    fontWeight: "bold"
                  }}
                  title={`Stop all ${getTotalActiveProcesses()} active processes`}
                >
                  {isLoading ? "Stopping…" : `Stop All (${getTotalActiveProcesses()})`}
                </button>
              )}
              
              {/* Status Messages */}
              {!primaryDisplayData && !hasMultipleProcesses() && !isLoading && !error && (
                <p className="status-message">No process active or selected. Go to "Start Analysis" to begin.</p>
              )}
              {isLoading && <p className="status-message">Updating status...</p>}
              {error && <p className="error-message">Error: {error}</p>}
            </div>
          </div>
          <div className="live-logs-area live-logs-container">
            <LiveLogs
              key={primaryDisplayData?.instanceId || "log-default"}
              {...logProps}
            />
          </div>
        </section>

        {allDisplayableItems.length > 0 && (
          <section className="tasks-table-section card-style">
            <h4 className="table-section-header">Active Tasks & Results ({allDisplayableItems.length})</h4>
            <div className="table-responsive-wrapper">
              <table className="tasks-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Task Name</th>
                    <th>Source/ID</th>
                    <th>Agent</th>
                    <th>Status</th>
                    <th>Latest Alert/Info</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allDisplayableItems.map(item => (
                    <TaskTableRow
                      key={item.key}
                      item={item}
                      isSelected={selectedPrimaryInstanceId === item.id}
                      onSelectItem={handleSelectItem}
                      onStopItem={handleStopProcess}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
         {!allDisplayableItems.length && !isLoading && (
            <section className="tasks-table-section card-style">
                 <h4 className="table-section-header">Active Tasks & Results (0)</h4>
                 <p style={{textAlign: 'center', padding: '20px'}}>No active tasks or results to display.</p>
            </section>
        )}
      </main>
    </div>
  );
};

export default ViewCameras;
